import { jobs } from '@/utils/request'

/**
 * 根据日程ID获取面试空间信息
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空字符串
 */
export const getInterviewSpaceIdAPI = async (scheduleId) => {
  const { data } = await jobs.get('/api/hr/v1/interview/schedule/spaceinfo', {
    params: {
      schedule_id: scheduleId
    }
  })
  return data.data || ''
}

/**
 * 根据面试空间ID获取面试空间的所有日程信息
 *
 * @param {string} spaceId - 面试空间的ID
 * @returns {Promise<Array>} 返回一个包含面试日程的数组
 */
export const getInterviewSpaceSchedulesAPI = async (spaceId) => {
  const { data } = await jobs.get('/api/hr/v1/interview/spaces/schedules', {
    params: {
      space_id: spaceId
    }
  })
  return data.data || []
}

/**
 * 根据日程ID获取该日程的所有文件信息
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空数组
 */
export const getScheduleFilesAPI = async (scheduleId) => {
  const { data } = await jobs.get('/api/hr/v1/interview/schedules/files', {
    params: {
      schedule_id: scheduleId
    }
  })
  return data.data || []
}

/**
 * 删除指定日程的文件
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空数组
 */
export const deleteScheduleFileAPI = async (scheduleId, fileList) => {
  await jobs.post('/api/hr/v1/interview/schedules/files/delete', {
    schedule_id: scheduleId,
    list: fileList
  })
}

/**
 * 向指定日程添加本地文件
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空数组
 */
export const uploadLocalFileAPI = async (scheduleId, fileList) => {
  const { data } = await jobs.post('/api/hr/v1/interview/schedules/files', {
    schedule_id: scheduleId,
    list: fileList
  })
  return data.data || {}
}

/**
 * 向指定日程添加云文档
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空数组
 */
export const uploadYunFileAPI = async ({ scheduleId, fileId, driveId, fileName, fileType }) => {
  const { data } = await jobs.post('/api/hr/v1/schedule/files/uploadfile', {
    schedule_id: scheduleId,
    file_id: fileId,
    drive_id: driveId,
    file_name: fileName,
    file_type: fileType
  })
  return data.data || {}
}


/**
 * 根据日程ID获取该日程的所有面试官信息
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空数组
 */
export const getScheduleUsersAPI = async (scheduleId) => {
  const { data } = await jobs.get('/api/hr/v1/interview/schedule/users', {
    params: {
      schedule_id: scheduleId
    }
  })
  return data.data || []
}

/**
 * 根据面试空间ID获取该面试空间的所有面试官信息
 *
 * @param {string} scheduleId - 面试日程的ID
 * @returns {Promise<string>} - 返回一个Promise对象，该对象解析为面试空间ID或在发生错误时返回空数组
 */
export const getSpaceUsersAPI = async (spaceId) => {
  const { data } = await jobs.get('/api/hr/v1/interview/space/users', {
    params: {
      space_id: spaceId
    }
  })
  return data.data || []
}

// 创建FP文档小记（笔记）
export const createAPNoteAPI = async (scheduleId) => {
  const { data } = await jobs.post('/api/hr/v1/notes/create', {
    schedule_id: scheduleId,
  })
  return data.data || {}
}

// 文件写入文本（目前仅支持FP）
export const writeAPNoteAPI = async ({ type, linkId, text }) => {
  const { data } = await jobs.post('/api/hr/v1/files/insert_text', {
    type, // plain或者markdown
    link_id: linkId,
    text // 文本内容, string
  })
  return data.data || {}
}

export const getInterviewMeetingAPI = async (spaceId) => {
  const { data } = await jobs.get('/api/hr/v1/interview/space/link', {
    params: {
      space_id: spaceId
    }
  })
  return data.data || {}
}

// 获取灵犀对话任务
export const getLingxiTaskAPI = async ({ scheduleId, spaceId, taskType }) => {
  try {
    const { data } = await jobs.get('/api/hr/v1/lingxi/tasks/get', {
      params: {
        schedule_id: scheduleId,
        space_id: spaceId,
        task_type: taskType, // 1: 面试大纲, 2: 面试评价
      }
    })
    _weblog.log('getLingxiTaskAPI 获取灵犀对话任务成功', data.data)
    return data.data || {}
  } catch (error) {
    _weblog.error('getLingxiTaskAPI 获取灵犀对话任务失败', error.message)
    return {}
  }
}

// 创建灵犀对话任务
export const createLingxiTaskAPI = async ({ scheduleId, spaceId, taskType }) => {
  const { data } = await jobs.post('/api/hr/v1/lingxi/tasks/create', {
    schedule_id: scheduleId,
    space_id: spaceId,
    task_type: taskType,
  })
  return data.data || {}
}

// 更新灵犀对话任务
export const updateLingxiTaskAPI = async ({ spaceId, taskId, taskStatus }) => {
  const { data } = await jobs.put('/api/hr/v1/lingxi/tasks/update', {
    space_id: spaceId,
    task_id: taskId,
    task_status: taskStatus, // 1: 等待, 2: 提交, 3: 运行中, 4: 已完成，5: 错误, 6: 已失效
  })
  return data.data || {}
}
